import { Box, Link, List, ListItem, Stack, Typography } from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { MdOutlinedFlag } from "react-icons/md";
import { HiOutlineTicket } from "react-icons/hi";

import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { createState } from "@/global/store/zustand_store";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose your games.",
    description: "Select the games you wish to attend.",
  },

  {
    icon: <TbShirtSport />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description:
      "Tickets will be available in your Ticketmaster Account. Reach out to Live Fan Support for any questions.",
  },
];

export const faqs = [
  {
    title: "When will I receive my tickets?",
    description:
      "If your reservation is accepted, a San Antonio FC Sports representative will be in contact with further information, including ticket delivery details.",
  },
  {
    title:
      "What happens after I purchase a San Antonio FC Sports Suite online?",
    description:
      "Following your purchase, a San Antonio FC Sports representative will follow up with you to confirm your order and assist with any questions you have in anticipation of your event. Tickets and parking will be added to your San Antonio FC Sports account where you'll be able to access them via Ticketmaster. All suite tickets are digital.",
  },
  {
    title: "What is included in my suite?",
    description:
      "If your reservation is accepted, a San Antonio FC Sports representative will be in contact with further information, including details regarding parking and food and beverage options.",
  },
];

export const SanAntonioFCSportsAdditionalDetailsDisclaimerTC = () => (
  <>
    By entering my name, checking the box above and clicking Submit Request, I
    hereby (i) agree to the{" "}
    <Link target="_blank" href="https://am.ticketmaster.com/sanantoniofc/terms">
      Terms of Use
    </Link>{" "}
    (ii) agree that my reservation will not be complete unless and until I
    receive a notice from the San Antonio FC Sports confirming whether or not my
    reservation has been accepted in accordance with our agreement.
  </>
);

export const inventoryCardSuiteModal = createState({
  title: "",
  isOpen: false,
  content: <></>,
});

export const SanAntonioFCSuitesInventoryCard = () => {
  const modal = inventoryCardSuiteModal();

  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      title={modal.title}
      hideSecondaryButton
      isOpen={modal.isOpen}
      renderFooter={() => null}
      onClose={() => modal.set({ isOpen: false })}
      paperProps={{ style: { marginLeft: 2, marginRight: 2 } }}
      renderBody={() => <Box p={1}>{modal.content}</Box>}
    />
  );
};

const SuiteContentHeader = ({ title }: { title: string }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Typography
      variant="h6"
      sx={{
        fontWeight: 600,
        minWidth: "135px",
        fontSize: "1.25rem",
        color: "primary.main",
      }}
    >
      {title}
    </Typography>
  </Stack>
);

export const TwentyPersonSuiteContent = () => (
  <Box p={2} pt={1}>
    <SuiteContentHeader title="20-person Suite" />
    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Tickets and Parking:</b> Each suite includes 20 tickets and 4)
          Lot 2 parking passes for your group to enjoy
          <br />• <b>Food and Beverage:</b> Includes $400 food and beverage
          credit to use with preordered catering or on matchday
          <br />• <b>Private Space:</b> Enjoy a private, climate-controlled
          environment complete with your own personal Suite Attendant to keep
          your group cared for from kick off to the final whistle
          <br />• <b>Seating:</b> Relax in interior and exterior seating
          featuring theater style seating on the balcony, perfect for taking in
          the action in total comfort
          <br />• <b>Added Bonus:</b> Group recognition on the videoboard at
          halftime
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const TwentyFivePersonSuiteContent = () => (
  <Box p={2} pt={1}>
    <SuiteContentHeader title="25-person Suite" />
    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Tickets and Parking:</b> Each suite includes 25 tickets and 5)
          Lot 2 parking passes for your group to enjoy
          <br />• <b>Food and Beverage:</b> Includes $500 food and beverage
          credit to use with preordered catering or on matchday
          <br />• <b>Private Space:</b> Enjoy a private, climate-controlled
          environment complete with your own personal Suite Attendant to keep
          your group cared for from kick off to the final whistle
          <br />• <b>Seating:</b> Relax in interior and exterior seating
          featuring theater style seating on the balcony, perfect for taking in
          the action in total comfort
          <br />• <b>Added Bonus:</b> Group recognition on the videoboard at
          halftime
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const SkySuiteContent = () => (
  <Box p={2} pt={1}>
    <SuiteContentHeader title="Sky Suite" />
    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Tickets:</b> The Sky Suite includes 90 tickets on the suite level
          <br />• <b>Food and Beverage:</b> Includes $1350 food and beverage
          credit to use with preordered catering or on matchday
          <br />• <b>Private Space:</b> Enjoy a private, mixed use space perfect
          for those who want to mingle while enjoying the game and access to the
          suite level amenities
          <br />• <b>Seating:</b> Relax in open air seating with a combination
          of high and low top tables and chairs
          <br />• <b>Added Bonus:</b> Group recognition on the videoboard at
          halftime
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const CornerKickZoneContent = () => (
  <Box p={2} pt={1}>
    <SuiteContentHeader title="CornerKick Zone" />
    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Tickets and Parking:</b> The CornerKick Zone includes 20 tickets
          and 4) Lot 1 parking passes for your group to enjoy
          <br />• <b>Food and Beverage:</b> $15 Credit per seat
          <br />• <b>Seating:</b> Relax private, reserved seating just steps
          away from amenities
          <br />• <b>Added Bonus:</b> Group recognition on the videoboard at
          halftime
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const PitchsideLoungeContent = () => (
  <Box p={2} pt={1}>
    <SuiteContentHeader title="Pitchside Lounge" />
    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Tickets and Parking:</b> The Pitchside Lounge includes 8 tickets
          and 2) Lot 1 parking passes for your group to enjoy
          <br />• <b>Food and Beverage:</b> $15 Credit per seat
          <br />• <b>Seating:</b> Relax private, reserved seating just steps
          away from amenities
          <br />• <b>Added Bonus:</b> Group recognition on the videoboard at
          halftime
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const PremiumBunkerContent = () => (
  <Box p={2} pt={1}>
    <SuiteContentHeader title="Premium Bunker" />
    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Tickets and Parking:</b> The Premium Bunker includes 50 tickets
          behind the South Goal
          <br />• <b>Food and Beverage:</b> All You Can Eat package
          <br />• <b>Seating:</b> Private, reserved space with field level
          seating and a semi-private bar
          <br />• <b>Added Bonus:</b> Group recognition on the videoboard at
          halftime
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const BunkerGAContent = () => (
  <Box p={2} pt={1}>
    <SuiteContentHeader title="Bunker GA" />
    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Tickets and Parking:</b> The Bunker GA includes 200 tickets
          behind the South Goal
          <br />• <b>Food and Beverage:</b> All You Can Eat package
          <br />• <b>Seating:</b> Reserved space with a combination of field
          level seating and standing room only spaces, access to a semi-private
          bar
          <br />• <b>Added Bonus:</b> Group recognition on the videoboard at
          halftime
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const twentyPersonSuiteNames = [
  "Suite 2",
  "Suite 3",
  "Suite 8",
  "Suite 9",
  "Suite 15",
];

export const twentyFivePersonSuiteNames = ["Suite 6"];

export const skySuiteNames = ["Sky Suite"];

export const cornerKickZoneNames = ["CornerKick Zone"];

export const pitchsideLoungeNames = ["Pitchside Lounge"];

export const premiumBunkerNames = ["Premium Bunker"];

export const bunkerGANames = ["Bunker GA"];

export const RegistrationTerms = () => (
  <>
    By creating an account you agree to be notified regarding your purchase and
    to the{" "}
    <Link
      target={"_blank"}
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href={
        "https://uploads-ssl.webflow.com/5ed7df6fe67e862e0a62f9f2/616882d2b691e09f75348396_Terms%20%26%20Conditions.pdf"
      }
    >
      Terms & Conditions
    </Link>{" "}
    and{" "}
    <Link
      href={
        "https://uploads-ssl.webflow.com/5ed7df6fe67e862e0a62f9f2/63c05d80816dac05b5b34ac7_seasonshare%20CCPA%20Privacy%20Policy%20Agreement.pdf"
      }
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
    >
      Privacy Policy
    </Link>
    .
  </>
);
